import { Badge, Button, CustomerWarrantyViewModal } from '@retainerclub/common';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import TableRowColumnAddress from './TableRowColumns/TableRowColumnAddress';
import TableRowColumnDetails from './TableRowColumns/TableRowColumnDetails';
import TableRowColumnShipping from './TableRowColumns/TableRowColumnShipping';

function DashboardShipmentsTableRow({
  shipment,
  handleOpenModal,
  handleWarrantyModalOpen,
}) {
  const { sendToClinic, address, productionOrderItems } = shipment;
  const [isViewPFGModalOpen, setIsViewPFGModalOpen] = useState(false);

  const _inactiveStates = [
    'customer_cancelled',
    'customer_completed',
    'customer_delivered',
    'customer_manufacturing',
    'customer_shipped',
    'customer_in_design',
    'customer_needs_attention',
    'customer_on_hold',
    'customer_collecting_shipment',
  ];

  function getStatusColor(status) {
    switch (status) {
      case 'customer_in_design':
      case 'customer_manufacturing':
        return 'green';
      case 'customer_collecting_shipment':
      case 'customer_shipped':
        return 'yellow';
      case 'customer_needs_attention':
      case 'customer_on_hold':
        return 'red';
      case 'customer_pending':
      case 'customer_delivered':
      case 'customer_cancelled':
      case 'customer_completed':
        return 'gray';
      default:
        return 'gray';
    }
  }

  function getShipmentName() {
    if (!productionOrderItems) {
      return <span className="font-medium">No items available</span>;
    }

    const consolidatedItems = productionOrderItems.reduce((acc, item) => {
      const existingItemIndex = acc.findIndex((i) => i.name === item.name);
      if (existingItemIndex !== -1) {
        // Create a new object instead of mutating the existing one
        acc[existingItemIndex] = {
          ...acc[existingItemIndex],
          quantity: acc[existingItemIndex].quantity + item.quantity,
        };
      } else {
        acc.push(item);
      }
      return acc;
    }, []);

    return consolidatedItems.map((item, index) => (
      <span key={item.id} className="font-medium">
        {item.name}{' '}
        <span className="font-normal text-gray-500 text-xs">
          ({item.quantity})
        </span>
        {index < consolidatedItems.length - 1 && ', '}
      </span>
    ));
  }

  const deliveredAt = useMemo(() => {
    const deliveredAts = productionOrderItems?.map((item) => item.deliveredAt);

    if (
      deliveredAts?.length === 0 ||
      deliveredAts === null ||
      deliveredAts === undefined ||
      deliveredAts?.every((at) => at === null)
    ) {
      return null;
    }

    return deliveredAts.find((at) => at !== null);
  }, [productionOrderItems]);

  const hasWarranties = useMemo(() => {
    return shipment.warranties && shipment.warranties.length > 0;
  }, [shipment.warranties]);

  function handleOpenViewPFGModal() {
    setIsViewPFGModalOpen(true);
  }

  function handleCloseViewPFGModal() {
    setIsViewPFGModalOpen(false);
  }

  const disableSubmitPFG = useCallback(() => {
    // One PFG per production order for customers; hide Issue? when already submitted
    if (hasWarranties) {
      return true;
    }

    // This production order was created from a PFG remake
    if (shipment.warrantyId) {
      return true;
    }

    // Disable warranty claims if explicitly disabled on production order
    if (shipment?.disableWarranty === true) {
      return true;
    }

    // Require at least one eligible item on the order (product.warrantyAllowed === true)
    const hasEligibleItem = (productionOrderItems || []).some(
      (item) => item?.product?.warrantyAllowed === true,
    );
    if (!hasEligibleItem) {
      return true;
    }

    const sixteenDaysAgo = new Date(Date.now() - 16 * 24 * 60 * 60 * 1000);
    const isPastDeadline = deliveredAt
      ? new Date(deliveredAt) < sixteenDaysAgo
      : true;

    const allowedStates = ['customer_delivered', 'customer_completed'];

    return (
      !allowedStates.includes(shipment.customerState) ||
      (allowedStates.includes(shipment.customerState) && isPastDeadline)
    );
  }, [
    hasWarranties,
    shipment.warrantyId,
    productionOrderItems,
    deliveredAt,
    shipment.customerState,
    shipment?.disableWarranty,
  ]);

  return (
    <tr>
      <td className="flex w-full flex-col justify-start gap-4 py-8 pr-3 pl-4 font-medium text-gray-900 text-sm sm:w-auto sm:max-w-none sm:flex-row sm:items-center sm:pl-0">
        <div>
          {getShipmentName()}
          <dl className="font-normal lg:hidden">
            <dt className="sr-only">Who&apos;s it for?</dt>
            <dd className="mt-1 truncate text-gray-700">
              <TableRowColumnDetails shipment={shipment} />
            </dd>
            <dt className="sr-only sm:hidden">Where&apos;s it going?</dt>
            <dd className="mt-1 truncate text-gray-500 sm:hidden">
              <TableRowColumnAddress
                address={address}
                sendToClinic={sendToClinic}
              />
            </dd>
            <dt className="sr-only sm:hidden">Shipping info</dt>
            <dd className="mt-1 truncate text-gray-500 sm:hidden">
              <TableRowColumnShipping
                productionOrderItems={productionOrderItems}
              />
            </dd>
          </dl>
        </div>
        <div>
          <Badge
            name={
              typeof shipment?.customerState === 'string'
                ? shipment.customerState
                    .replace('customer_', '')
                    .split('_')
                    .join(' ')
                : ''
            }
            withDot={false}
            size="sm"
            color={getStatusColor(shipment?.customerState ?? 'unknown')}
          />
        </div>
      </td>
      <td className="hidden gap-2 px-3 py-8 text-gray-500 text-sm sm:table-cell">
        <TableRowColumnDetails shipment={shipment} />
      </td>

      <td className="hidden px-3 py-8 text-gray-500 text-sm sm:table-cell">
        <TableRowColumnAddress address={address} sendToClinic={sendToClinic} />
      </td>
      <td className="hidden px-3 py-8 text-gray-500 text-sm sm:table-cell">
        <TableRowColumnShipping
          shipment={shipment}
          productionOrderItems={productionOrderItems}
        />
      </td>
      <td className="py-8 pr-4 pl-3 text-right font-medium text-sm sm:pr-0">
        {hasWarranties ? (
          <>
            <Button
              size="sm"
              color="rcprimary"
              name={
                shipment.warranties?.length === 1 ? 'View PFG' : 'View PFGs'
              }
              rounded
              className="whitespace-nowrap"
              onClick={handleOpenViewPFGModal}
            />
            <CustomerWarrantyViewModal
              isOpen={isViewPFGModalOpen}
              onClose={handleCloseViewPFGModal}
              warranties={shipment.warranties}
            />
          </>
        ) : !disableSubmitPFG() ? (
          <Button
            size="sm"
            color="rcprimary"
            name="Issue?"
            rounded
            onClick={() => handleWarrantyModalOpen(shipment)}
          />
        ) : (
          <Button
            size="sm"
            color="rcprimary"
            name="Manage"
            rounded
            disabled
            onClick={() => handleOpenModal(shipment)}
          />
        )}
      </td>
    </tr>
  );
}

DashboardShipmentsTableRow.propTypes = {
  shipment: PropTypes.object.isRequired,
  handleOpenModal: PropTypes.func.isRequired,
  handleWarrantyModalOpen: PropTypes.func.isRequired,
};

export default DashboardShipmentsTableRow;
